import { Injectable } from '@angular/core';
import * as ProfileAction from './profile.actions';
import { Store } from '@ngrx/store';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { ExperienceData, OldExperienceData, PlayerTag, ProfileComponent, ProfileComponentId, ProfileData, ProfileState } from './profile.state';
import { filter, map, distinctUntilChanged, takeUntil, pluck, shareReplay } from 'rxjs/operators';
import { AppState } from '../app.state';
import { doc, Firestore, collection, collectionData, docData } from '@angular/fire/firestore';
import { FireSubService } from '../../firebase/fireSub.service';
import { CurrencyType, IInviteXP, INextDailyDates } from '@soposopo/sopo-models';

export const USER_NAME_MIN_LENGTH = 3;
export const USER_NAME_MAX_LENGTH = 12;
export const USER_NAME_ACCEPTED_CHARACTERS = '^[a-zA-Z0-9]+$';

@Injectable({
    providedIn: 'root'
})
export class ProfileStateDispatcher {
    private diamondSubscription: Subscription;
    private tagSubscription: Subscription;
    private experienceSubscription: Subscription;

    private _profileState$ = this.store
        .select((state: AppState) => state.profile)
        .pipe(
            shareReplay({ refCount: true, bufferSize: 1 }),
            filter((profileState) => profileState.profileData != null),
        );

    constructor(
        private store: Store<AppState>,
        private firestore: Firestore,
        private fireSubService: FireSubService,
    ) {}

    /** Returns an Observable of profile state with non-null profileData */
    public getProfileState(): Observable<ProfileState> {
        return this._profileState$;
    }

    public getUnfilteredProfileDataObservable(): Observable<ProfileData> {
        return this.store.select((state: AppState) => state.profile).pipe(
            map(profileState => profileState.profileData),
            distinctUntilChanged((prev, next) => {
                return prev?.displayName === next?.displayName &&
                    prev?.useNftAsAvatar === next?.useNftAsAvatar &&
                    prev?.nft?.usingCroppedImage === next?.nft?.usingCroppedImage &&
                    prev?.nft?.imageUrl === next?.nft?.imageUrl &&
                    prev?.photoURL === next?.photoURL
            }),
        );
    }

    /**  Returns the clients profile ID as a promise*/
    public getMyID(): Promise<string> {
        return firstValueFrom(this.getProfileState()
            .pipe(
                map((profileState) => (profileState.profileData ? profileState.profileData.uid : '')),
                filter((uid) => uid.length > 0),
            ));
    }

    /**  Returns the clients profile ID as an observable*/
    public getMyIDObservable(): Observable<string> {
        return this.getProfileState().pipe(
            map((profileState) => (profileState.profileData?.uid || '')),
            distinctUntilChanged(),
        );
    }

    public getMyShortID(): Promise<number> {
        return firstValueFrom(this.getProfileState()
            .pipe(
                map((profileState) => (profileState.profileData ? profileState.profileData.shortID : 0)),
                filter((shortID) => shortID > 0),
            ));
    }

    public getUserLoginStreak(): Observable<number> {
        return this.getProfileState()
            .pipe(
                map(profileState => profileState.profileData.streak)
            );
    }

    public updateProfile(userID: string): void {
        if (this.diamondSubscription) {
            this.diamondSubscription.unsubscribe();
        }

        if(userID) {
            const docReference = doc(this.firestore, `users/${userID}`);
            this.diamondSubscription = docData(docReference)
                .pipe(
                    takeUntil(this.fireSubService.unsubscriber),
                    filter((data) => data != null))
                .subscribe((data: any) => {
                    this.store.dispatch(ProfileAction.updateCurrenciesActio({diamonds: data.diamonds,golds: data.golds}));
            });
        }
    }

    createSubscirptions(userId: string) {
        this.createExperienceSubscription(userId);
        this.createTagsSubsciption(userId);
    }

    createTagsSubsciption(userId: string) {
        if (this.tagSubscription) {
            this.tagSubscription.unsubscribe();
        }

        if(userId){
            const collectionReference = collection(this.firestore, `users/${userId}/tags`);
            this.tagSubscription = collectionData(collectionReference, {idField: 'playerId'})
                .subscribe(tagsData => {
                    let tagsObj = {};
                    tagsData.map(data => {
                        tagsObj[data.playerId] = {note: '', selectedColor: -1, mutedInChat: false};
                        if (data.note) tagsObj[data.playerId].note = data.note;
                        if (data.selectedColor != undefined) tagsObj[data.playerId].selectedColor = data.selectedColor;
                        if (data.mutedInChat) tagsObj[data.playerId].mutedInChat = data.mutedInChat;
                    })

                    this.store.dispatch(ProfileAction.setProfileTagsAction({tags: tagsObj}));
                })
            this.fireSubService.registerSubscription(this.tagSubscription);
        }
    }

    createExperienceSubscription(userId: string) {
        if (this.experienceSubscription) {
            this.experienceSubscription.unsubscribe();
        }

        if(userId){
            const collectionRef = doc(this.firestore, `experience/${userId}/`);
            this.experienceSubscription = docData(collectionRef)
                .subscribe((experience: ExperienceData) => {
                    if (!experience) return;
                    let experienceData: ExperienceData = { totalEarnedXP: 0, convertedXP: 0, burnXP: 0, inviteXP: {}, invitedBy: null, totalWagesSB: 0, loyaltyPointByHand: 0, missionXP: 0, invitedUsersBonusXP: 0, welcomeBonusXP: 0, baseWheelSpinReward: 0, jackpotWheelSpinReward: 0, dailyWheelSpinReward: 0 };

                    if(experience.totalEarnedXP) experienceData.totalEarnedXP = experience.totalEarnedXP;
                    if(experience.convertedXP) experienceData.convertedXP = experience.convertedXP;
                    if(experience.burnXP) experienceData.burnXP = experience.burnXP;
                    if(experience.inviteXP) experienceData.inviteXP = experience.inviteXP;
                    if(experience.invitedBy) experienceData.invitedBy = experience.invitedBy;
                    if(experience.totalWagesSB) experienceData.totalWagesSB = experience.totalWagesSB;
                    if(experience) experienceData.totalWagesSB = experience.totalWagesSB;
                    if(experience.loyaltyPointByHand) experienceData.loyaltyPointByHand = experience.loyaltyPointByHand;
                    if(experience.missionXP) experienceData.missionXP = experience.missionXP;
                    if(experience.invitedUsersBonusXP) experienceData.invitedUsersBonusXP = experience.invitedUsersBonusXP;
                    if(experience.welcomeBonusXP) experienceData.welcomeBonusXP = experience.welcomeBonusXP;
                    if(experience.baseWheelSpinReward) experienceData.baseWheelSpinReward = experience.baseWheelSpinReward;
                    if(experience.jackpotWheelSpinReward) experienceData.jackpotWheelSpinReward = experience.jackpotWheelSpinReward;
                    if(experience.dailyWheelSpinReward) experienceData.dailyWheelSpinReward = experience.dailyWheelSpinReward;

                    this.store.dispatch(ProfileAction.setExperienceAction({experienceData}));
                })
            this.fireSubService.registerSubscription(this.experienceSubscription);
        }
    }

    public getExperienceObservable(): Observable<ExperienceData | OldExperienceData> {
        return this.getProfileState().pipe(map(profileState => profileState.experience), distinctUntilChanged())
    }

    public getReferralEarningsObservable(): Observable<{[invitedUserId: string]: IInviteXP} | {}> {
        return this.getExperienceObservable().pipe(map(experienceData => experienceData.inviteXP));
    }

    public getMutedInChat(playerId: string) {
        return firstValueFrom(this.getMutedInChatObservable(playerId));
    }

    public getMutedInChatObservable(playerId: string): Observable<boolean> {
        return this.getProfileState().pipe(
            map((profileState) => (profileState.tags[playerId] ? profileState.tags[playerId].mutedInChat : false)),
            distinctUntilChanged(),
        );
    }

    public getMutedInChatPlayerIdsObservable(): Observable<string[]> {
        return this.getProfileState().pipe(
            map((profileState) => {
                const keys = Object.keys(profileState.tags);
                let playerIds: string[] = [];
                keys.forEach(key => {
                    if(profileState.tags[key].mutedInChat) playerIds.push(key);
                })
                return playerIds;
            }),
            distinctUntilChanged((prev, curr) => prev.length === curr.length),
        )
    }

    public getUserTagObservable(playerId: string): Observable<PlayerTag> {
        return this.getProfileState().pipe(
            map(profileState => profileState.tags[playerId] ?
                { note: profileState.tags[playerId].note, selectedColor: profileState.tags[playerId].selectedColor } :
                {note: '', selectedColor: -1}
            ),
            distinctUntilChanged((x,y) => x.note === y.note && x.selectedColor === y.selectedColor)
        )
    }

    public getDiamond(): Observable<number> {
        return this.getProfileState().pipe(
            map((profileState) => profileState.profileData!.diamonds),
            distinctUntilChanged(),
        );
    }

    public fetchProfile(userId: string): void {
        this.store.dispatch(ProfileAction.profileRequestedAction({profileId: userId}));
    }

    public clearProfile(): void {
        this.store.dispatch(ProfileAction.clearProfileAction());
    }

    public setDefaultAvatar(defaultAvatar: boolean): void {
        this.store.dispatch(ProfileAction.profileSetDefaultAvatarAction({defaultAvatar}));
    }

    public updateNextDailyAndNextStreak(props: INextDailyDates) {
        this.store.dispatch(ProfileAction.updateNextDailyAndNextStreakAction({timestamps: props}));
    }

    public enableGPSLocation(gps: boolean): void {
        this.store.dispatch(ProfileAction.profileSetGPSAction({gps}));
    }
    public setLastNameChange(): void {
        this.store.dispatch(ProfileAction.profileSetLastNameChangeAction());
    }

    public acceptTermsOnRegistrationPage(): void {
        this.store.dispatch(ProfileAction.acceptTermsOnRegistrationPage());
    }

    public getNextDailyBonus(): Observable<number> {
        return this.getProfileState().pipe(map(data => data.timestamps.nextDailyBonus));
    }

    public getNextStreak(): Observable<number> {
        return this.getProfileState().pipe(map(data => data.timestamps.nextStreak));
    }

    public getActivatedComponent(): Observable<ProfileComponent> {
        return this.getProfileState().pipe(map(data => data.activeComponent));
    }

    public activateComponent(componentId: ProfileComponentId) {
        this.store.dispatch(ProfileAction.activateProfileComponent({component: {componentId}}));
    }

    public getBalanceByCurrency(currency: CurrencyType): Observable<number> {
        return this.getProfileState().pipe(
            map((profileState) => {
                if (profileState.profileData && profileState.profileData[currency])
                    return profileState.profileData[currency];
                else return 0;
            }));
    }

    public getProfileSetupNeeded(): Observable<boolean | null> {
        return this.getProfileState().pipe(map((profileState) => profileState.profileData.profileSetupNeeded));
    }

    public unsubscribeProfile() {
        if (this.diamondSubscription) {
            this.diamondSubscription.unsubscribe();
        }
        if (this.tagSubscription) {
            this.tagSubscription.unsubscribe();
        }
        if (this.experienceSubscription) {
            this.experienceSubscription.unsubscribe();
        }
    }
}
